import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  Stack,
  Paper,
  CardMedia,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { imgUrl, TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import { IAnnoucement } from 'src/Types/module/announcement/announcementType';
import CommonPdfAndImageVeiw from 'src/components/pdfAndImageView/CommonPdfAndImageVeiw';
import {
  Attachment as AttachmentIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { TbFileText } from 'react-icons/tb';

export default function ElectionBoardNotice() {
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [notices, setNotices] = useState<IAnnoucement[]>([]);
  const [activeNotice, setActiveNotice] = useState<any>(null);
  const [selectedNoticeId, setSelectedNoticeId] = useState<number | ''>('');

  const Toast = Toaster();
  const token = localStorage.getItem(TOKEN_KEY);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Fetch active election board notice
      const activeUri = `${url}/api/v1/admin/announcement/election-board-notice`;
      const activeRes = await axios.get(activeUri, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (activeRes.data?.success && activeRes.data?.data) {
        setActiveNotice(activeRes.data.data);
        setSelectedNoticeId(
          activeRes.data.data.announcement_id || activeRes.data.data.id || '',
        );
      } else {
        setActiveNotice(null);
        setSelectedNoticeId('');
      }
      // 2. Fetch all notices for the dropdown selection
      const noticesUri = `${url}/api/v1/admin/announcement`;
      const noticesRes = await axios.get(noticesUri, {
        params: { type: 'NOTICE' },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (noticesRes.data?.success) {
        setNotices(noticesRes.data.data || []);
      }
    } catch (error: any) {
      Toast.fire({
        icon: 'error',
        title:
          error?.response?.data?.message ||
          'Failed to fetch election board data',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    if (!selectedNoticeId) {
      Toast.fire({
        icon: 'warning',
        title: 'Please select a notice first',
      });
      return;
    }

    setSubmitting(true);
    try {
      const saveUri = `${url}/api/v1/admin/announcement/election-board-notice`;
      const res = await axios.post(
        saveUri,
        { announcement_id: selectedNoticeId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data?.success) {
        Toast.fire({
          icon: 'success',
          title: res.data?.message || 'Election board notice set successfully!',
        });
        fetchData();
      } else {
        Toast.fire({
          icon: 'error',
          title: res.data?.message || 'Failed to set election board notice',
        });
      }
    } catch (error: any) {
      Toast.fire({
        icon: 'error',
        title: error?.response?.data?.message || 'Something went wrong',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight={300}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card sx={{ mx: 3 }}>
      <CardHeader title='Election Board Notice Configuration' />
      <Divider />
      <CardContent sx={{ p: 4 }}>
        <Grid container spacing={4}>
          {/* Active Notice Details */}
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.02)'
                    : '#f8fafc',
                minHeight: '350px',
              }}
            >
              <Typography
                variant='h4'
                sx={{ mb: 2, color: 'primary.main', fontWeight: 700 }}
              >
                Active Election Board Notice Info
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {activeNotice ? (
                <Box>
                  {activeNotice.thumbnail &&
                    activeNotice.thumbnail !== 'undefined' && (
                      <CardMedia
                        component='img'
                        image={`${imgUrl}/${activeNotice.thumbnail}`}
                        alt='Notice Thumbnail'
                        sx={{
                          width: '100%',
                          maxHeight: '200px',
                          objectFit: 'cover',
                          borderRadius: 1,
                          mb: 2,
                        }}
                      />
                    )}

                  <Typography variant='h5' sx={{ mb: 1, fontWeight: 'bold' }}>
                    {activeNotice.title}
                  </Typography>

                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 2 }}
                  >
                    Published:{' '}
                    {activeNotice.published_date
                      ? new Date(
                          activeNotice.published_date,
                        ).toLocaleDateString()
                      : 'N/A'}
                  </Typography>

                  <Typography
                    variant='body1'
                    sx={{
                      mb: 3,
                      color: 'text.secondary',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {activeNotice.content}
                  </Typography>

                  {activeNotice.attachments &&
                    activeNotice.attachments.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Stack
                          direction='row'
                          alignItems='center'
                          spacing={1}
                          sx={{ mb: 1.5 }}
                        >
                          <AttachmentIcon color='action' />
                          <Typography variant='h5' sx={{ fontWeight: 700 }}>
                            Attachments
                          </Typography>
                        </Stack>
                        <Grid container spacing={2}>
                          {activeNotice.attachments.map((file: any) => {
                            const isPdf = file.url
                              ?.toLowerCase()
                              .endsWith('.pdf');
                            const fileName =
                              file.url?.split('/').pop() || 'document';
                            return (
                              <Grid item xs={12} sm={6} key={file.id}>
                                <Paper
                                  variant='outlined'
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 1.5,
                                    borderRadius: '12px',
                                    bgcolor: 'background.paper',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    {isPdf ? (
                                      <TbFileText size={20} color='#3b82f6' />
                                    ) : (
                                      <ImageIcon
                                        color='primary'
                                        fontSize='small'
                                      />
                                    )}
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: 120,
                                      }}
                                    >
                                      {fileName}
                                    </Typography>
                                  </Box>
                                  <CommonPdfAndImageVeiw
                                    file={file.url}
                                    fileName='View'
                                  />
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    )}
                </Box>
              ) : (
                <Box
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                  sx={{ minHeight: '200px' }}
                >
                  <Typography
                    variant='body1'
                    color='text.secondary'
                    sx={{ fontStyle: 'italic' }}
                  >
                    No active election board notice set yet. Please select one
                    from the right section.
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Select Notice Form */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: '350px',
              }}
            >
              <Box>
                <Typography variant='h4' sx={{ mb: 1.5, fontWeight: 700 }}>
                  Select Notice
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 3 }}
                >
                  Choose a notice from the dropdown list to set it as the active
                  Election Board Notice. Only announcements of type NOTICE are
                  listed.
                </Typography>

                <FormControl fullWidth variant='outlined'>
                  <InputLabel id='select-notice-label'>
                    Choose Notice
                  </InputLabel>
                  <Select
                    labelId='select-notice-label'
                    value={selectedNoticeId}
                    onChange={(e) =>
                      setSelectedNoticeId(Number(e.target.value))
                    }
                    label='Choose Notice'
                  >
                    <MenuItem value=''>
                      <em>None</em>
                    </MenuItem>
                    {notices.map((notice) => (
                      <MenuItem key={notice.id} value={notice.id}>
                        {notice.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Button
                variant='contained'
                color='primary'
                onClick={handleSave}
                disabled={submitting || !selectedNoticeId}
                size='large'
                fullWidth
                sx={{ py: 1.5, mt: 4 }}
              >
                {submitting ? 'Setting Active Notice...' : 'Set Active Notice'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
